import React, { useEffect, useState } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Badge,
  Box,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Avatar,
} from '@mui/material';
import School from '@mui/icons-material/School';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import Logout from '@mui/icons-material/Logout';
import Dashboard from '@mui/icons-material/Dashboard';
import Store from '@mui/icons-material/Store';
import Menu from '@mui/icons-material/Menu';
import Close from '@mui/icons-material/Close';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Receipt from '@mui/icons-material/Receipt';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import Settings from '@mui/icons-material/Settings';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useSchoolSettings } from '../../contexts/SchoolSettingsContext';
import { NotificationCenter } from './NotificationCenter';

interface AppBarProps {
  onNavigate: (path: string) => void;
}

export const AppBar: React.FC<AppBarProps> = ({ onNavigate }) => {
  const { user, student, parent, logout, isAuthenticated, openStudentSelector } = useAuth();
  const { itemCount } = useCart();
  const { settings } = useSchoolSettings();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleLogout = () => {
    logout();
    onNavigate('/');
    setMobileMenuOpen(false);
  };

  const handleNavigation = (path: string) => {
    onNavigate(path);
    setMobileMenuOpen(false);
  };

  // Tour-driven cart navigation. When the cursor "clicks" the cart
  // icon in step 7, this trigger fires and we navigate programmatically
  // (the cursor click is visual-only — no real DOM click event).
  useEffect(() => {
    const onTrigger = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.trigger === 'navigate-to-cart') {
        onNavigate('/cart');
      }
    };
    window.addEventListener('cc-tour-trigger', onTrigger);
    return () => window.removeEventListener('cc-tour-trigger', onTrigger);
  }, [onNavigate]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleSwitchStudent = () => {
    openStudentSelector();
    setMobileMenuOpen(false);
  };

  return (
    <MuiAppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: '#ffffff',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderRadius: 0,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar sx={{ minHeight: { xs: 64, md: 72 }, px: { xs: 1, sm: 2, md: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 1, sm: 2, md: 4 } }}>
            <IconButton
              edge="start"
              onClick={() => handleNavigation(isAuthenticated ? '/dashboard' : '/')}
              sx={{
                mr: { xs: 1, sm: 2 },
                p: 0.5,
              }}
            >
              {settings.logoUrl ? (
                <Avatar
                  src={settings.logoUrl}
                  sx={{ width: { xs: 36, md: 44 }, height: { xs: 36, md: 44 } }}
                />
              ) : (
                <Box
                  sx={{
                    width: { xs: 36, md: 44 },
                    height: { xs: 36, md: 44 },
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${settings.gradientStartColor} 0%, ${settings.gradientEndColor} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <School sx={{ fontSize: { xs: 20, md: 24 }, color: 'white' }} />
                </Box>
              )}
            </IconButton>

            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 700, letterSpacing: '-0.01em', color: 'text.primary', fontSize: { sm: '1rem', md: '1.25rem' } }}>
                {settings.schoolName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: { xs: 'none', md: 'block' } }}>
                Fee Management Portal
              </Typography>
            </Box>
          </Box>

          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mx: 'auto' }}>
              {isAuthenticated && (
                <>
                  <Button
                    startIcon={<Dashboard />}
                    onClick={() => handleNavigation('/dashboard')}
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      color: 'text.primary',
                      '&:hover': {
                        bgcolor: 'grey.100',
                      },
                    }}
                  >
                    Dashboard
                  </Button>

                  <Button
                    startIcon={<Store />}
                    onClick={() => handleNavigation('/catalog')}
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      color: 'text.primary',
                      '&:hover': {
                        bgcolor: 'grey.100',
                      },
                    }}
                  >
                    Fee Catalog
                  </Button>

                  <Button
                    startIcon={<Receipt />}
                    onClick={() => handleNavigation('/purchases')}
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      color: 'text.primary',
                      '&:hover': {
                        bgcolor: 'grey.100',
                      },
                    }}
                  >
                    Purchases
                  </Button>
                </>
              )}

              <Button
                startIcon={<CalendarMonth />}
                onClick={() => handleNavigation('/events')}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                }}
              >
                Events
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, ml: 'auto' }}>
            {isAuthenticated && <NotificationCenter userId={user?.id} />}

            <IconButton
              data-tour="header-cart-icon"
              onClick={() => handleNavigation('/cart')}
              sx={{
                color: 'text.primary',
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
            >
              <Badge
                badgeContent={itemCount}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontWeight: 700,
                  },
                }}
              >
                <ShoppingCart />
              </Badge>
            </IconButton>

            {isAuthenticated && !isMobile && (
              <Box data-tour="student-switcher" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'text.primary' }}>
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                    {student ? `Managing: ${student.firstName} ${student.lastName}` : user?.email}
                  </Typography>
                </Box>

                {parent && parent.studentIds.length > 1 && (
                  <IconButton
                    onClick={handleSwitchStudent}
                    sx={{
                      color: 'text.primary',
                      '&:hover': {
                        bgcolor: 'grey.100',
                      },
                    }}
                    title="Switch Student"
                  >
                    <SwapHoriz />
                  </IconButton>
                )}

                <IconButton
                  onClick={() => handleNavigation('/account')}
                  sx={{
                    color: 'text.primary',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                  title="Account Settings"
                >
                  <Settings />
                </IconButton>

                <IconButton
                  onClick={handleLogout}
                  sx={{
                    color: 'text.primary',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                >
                  <Logout />
                </IconButton>
              </Box>
            )}

            {isMobile && (
              <IconButton
                onClick={toggleMobileMenu}
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                }}
              >
                {mobileMenuOpen ? <Close /> : <Menu />}
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </Container>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            pt: 2,
          },
        }}
      >
        <Box sx={{ px: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Menu
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
          {isAuthenticated && user && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {student ? `Managing: ${student.firstName} ${student.lastName}` : user.email}
              </Typography>
              {parent && parent.studentIds.length > 1 && (
                <Button
                  size="small"
                  startIcon={<SwapHoriz />}
                  onClick={handleSwitchStudent}
                  sx={{ mt: 1, textTransform: 'none' }}
                  fullWidth
                >
                  Switch Student
                </Button>
              )}
            </Box>
          )}
          {!isAuthenticated && (
            <Box sx={{ p: 2, bgcolor: '#e0f2fe', borderRadius: 2, border: '1px solid #0891b2' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#0e7490' }}>
                Guest User
              </Typography>
              <Typography variant="caption" sx={{ color: '#0e7490' }}>
                Browsing as a guest
              </Typography>
            </Box>
          )}
        </Box>

        <Divider />

        <List>
          {isAuthenticated && (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNavigation('/dashboard')}>
                  <ListItemIcon>
                    <Dashboard />
                  </ListItemIcon>
                  <ListItemText primary="Dashboard" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNavigation('/catalog')}>
                  <ListItemIcon>
                    <Store />
                  </ListItemIcon>
                  <ListItemText primary="Fee Catalog" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNavigation('/purchases')}>
                  <ListItemIcon>
                    <Receipt />
                  </ListItemIcon>
                  <ListItemText primary="Purchases" />
                </ListItemButton>
              </ListItem>
            </>
          )}

          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/events')}>
              <ListItemIcon>
                <CalendarMonth />
              </ListItemIcon>
              <ListItemText primary="Events" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton onClick={() => handleNavigation('/cart')}>
              <ListItemIcon>
                <Badge badgeContent={itemCount} color="error">
                  <ShoppingCart />
                </Badge>
              </ListItemIcon>
              <ListItemText primary="Shopping Cart" />
            </ListItemButton>
          </ListItem>
        </List>

        {isAuthenticated && (
          <>
            <Divider />

            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNavigation('/account')}>
                  <ListItemIcon>
                    <Settings />
                  </ListItemIcon>
                  <ListItemText primary="Account Settings" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton onClick={handleLogout}>
                  <ListItemIcon>
                    <Logout />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </ListItem>
            </List>
          </>
        )}
      </Drawer>
    </MuiAppBar>
  );
};
